<i18n
  src="@/locales/views/customer/quality_control/destination/new_inspect.yml"
  lang="yaml"
></i18n>

<i18n src="@/locales/models/quality_inspect.yml" lang="yaml"></i18n>
<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      name="customer_quality_control_destination_list_inspects_path"
      :params="{
        customer_id: customer_id,
        container_id: container_id,
        agro_exporter_id: agro_exporter_id,
        current_tab: current_tab,
      }"
    />

    <bread-crumbs
      :labels="[
        module_name,
        agro_exporter_process.name,
        container.container_code,
        template?.name,
      ]"
    ></bread-crumbs>

    <template v-if="load_data_general">
      <container-info :container="container" />
      <div class="mt-8">
        <div class="panel">
          <div class="panel-header">
            <h3 class="panel-header-title">
              {{ $t("labels.title") }}
            </h3>
          </div>

          <div class="panel-body">
            <div
              class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 w-full pb-6"
            >
              <div class="sm:col-span-3">
                <label
                  for="first-name"
                  class="block text-sm font-medium text-gray-700"
                >
                  {{ $t("model.quality_inspect.registered_at") }}
                </label>
                <div class="mt-1 relative rounded-md shadow-sm z-10">
                  <date-form
                    v-model="registered_at"
                    :first-day-of-week="2"
                    locale="es"
                    show-iso-weeknumbers
                  ></date-form>
                </div>
              </div>
              <div class="sm:col-span-3" v-if="template?.control_destination">
                <label
                  for="origin_export_id"
                  class="block text-sm font-medium text-gray-700"
                >
                  Productor(a)
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="exported_batch_options.length > 1"
                    v-model="exported_batch_id"
                    :options="exported_batch_options"
                  />
                  <div
                    class="content-data"
                    v-else-if="exported_batch_options.length == 1"
                  >
                    {{ exported_batch_options[0]?.name }}
                  </div>
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="
                  template &&
                  template?.use_samplings &&
                  !template?.specific_sampling
                "
              >
                <label
                  for="first-name"
                  class="block text-sm font-medium text-gray-700"
                >
                  {{ $t("model.quality_inspect.samplings") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.lazy.number="samplings"
                    class="input-text"
                  />
                </div>
              </div>
            </div>
            <template v-if="template">
              <sampling-layout
                v-if="template.use_samplings"
                v-model="quality_samplings"
                :samplings="samplings"
                :quality_template="template"
              />
              <general-layout
                v-else
                v-model="quality_generals"
                :quality_template="template"
              />
            </template>
          </div>
          <div class="panel-footer">
            <div class="panel-footer-actions">
              <white-button
                @click.prevent="gotToContainer()"
                :label="$t('labels.cancel_button')"
              />
              <span class="space-actions"></span>
              <success-button
                v-if="!proc_save_button"
                @click.prevent="submit()"
                :label="$t('labels.save_button')"
              />
              <success-button-processing
                v-else
                :label="$t('labels.save_button')"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <loading-message v-else />
  </div>
</template>

<script>
import Requester from "@/views/customer/requesters/quality_control/container/new_inspect.js";
import ContainerInfo from "@/components/views/customer/quality_control/destination/ContainerInfo.vue";
import GoBackLink from "@/components/views/layout/GoBackLink";
import SamplingLayout from "@/components/views/layout/quality_control/SamplingLayout.vue";
import GeneralLayout from "@/components/views/layout/quality_control/GeneralLayout.vue";

import moment from "moment";

export default {
  components: {
    ContainerInfo,
    GoBackLink,
    GeneralLayout,
    SamplingLayout,
  },
  props: {
    customer_id: { required: true },
    container_id: {
      required: true,
    },
    quality_template_id: {
      required: true,
    },
    exporter_process_quality_template_id: {
      required: true,
    },
    agro_exporter_id: {
      required: true,
    },
    current_tab: {},
  },
  data() {
    return {
      load_data_general: false,
      proc_save_button: false,
      agro_exporter_process: {},
      container: {},
      template: null,
      samplings: null,
      registered_at: null,
      exported_batch: null,
      exported_batch_id: null,
      exported_batches: [],
      exported_batch_options: [],
      quality_generals: [],
      quality_samplings: [],
    };
  },
  computed: {
    can_send: function () {
      return this.valid_data && !this.proc_save_button && this.template;
    },
    groups: function () {
      let groups = [];
      if (this.template)
        groups = this.template.quality_template_groups.map(
          (tp) => tp.quality_group
        );
      return groups;
    },
    registered_at_formatted: function () {
      return moment(this.registered_at).format("YYYY-MM-DD");
    },
    valid_data: function () {
      return (
        Boolean(this.registered_at) &&
        (Boolean(this.samplings) || !this.template?.use_samplings) &&
        Boolean(this.exported_batch_id)
      );
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          customer_id: this.customer_id,
        },
      }).then(() => {
        this.registered_at = moment().format("YYYY-MM-DD");
        this.fetchAgroExporter();
        this.fetchContainer();
        this.fetchTemplate().then(() => {
          if (this.template?.control_destination) this.fetchExportedBatches();
        });
      })
    );
  },
  methods: {
    fetchAgroExporter: async function () {
      let response = await Requester.fetchAgroExporter({
        customer_id: this.customer_id,
        agro_exporter_id: this.agro_exporter_id,
      });
      this.agro_exporter_process = response.data;
    },
    exportedBatchOption: function (exported_batch) {
      let label = "";
      if (exported_batch.producer) {
        label += exported_batch.producer;
        if (exported_batch.organization)
          label += ` (${exported_batch.organization})`;
      } else label += exported_batch.organization;
      return {
        name: label,
        id: exported_batch.id,
      };
    },
    fetchContainer: async function () {
      let response = await Requester.fetchContainer({
        customer_id: this.customer_id,
        agro_exporter_id: this.agro_exporter_id,
        container_id: this.container_id,
      });
      this.container = response.data;
    },
    fetchExportedBatches: async function () {
      let response = await Requester.fetchExportedBatches({
        customer_id: this.customer_id,
        container_id: this.container_id,
      });
      this.exported_batches = response.data;
      this.exported_batches.forEach((exported_batch) => {
        this.exported_batch_options.push(
          this.exportedBatchOption(exported_batch)
        );
      });
      if (this.exported_batches.length == 1)
        this.exported_batch_id = this.exported_batches[0]?.id;
    },
    fetchTemplate: async function () {
      let response = await Requester.fetchTemplate({
        customer_id: this.customer_id,
        agro_exporter_id: this.agro_exporter_id,
        container_id: this.container_id,
        exporter_process_quality_template_id:
          this.exporter_process_quality_template_id,
      });
      this.template = response.data;
      if (this.template?.specific_sampling)
        this.samplings = this.template?.samplings;
      this.load_data_general = true;
    },
    gotToContainer: function () {
      this.$router.push({
        name: "customer_quality_control_destination_list_inspects_path",
        params: {
          container_id: this.container_id,
          agro_exporter_id: this.agro_exporter_id,
          exporter_process_quality_template_id:
            this.exporter_process_quality_template_id,
        },
      });
    },
    submit: async function () {
      if (!this.can_send) return;
      this.proc_save_button = true;
      if (this.template?.use_samplings) await this.submit_with_samplings();
      else await this.submit_without_samplings();
      this.proc_save_button = false;
    },
    submit_with_samplings: async function () {
      try {
        let response = await Requester.createWithSamplings({
          container_id: this.container_id,
          exporter_process_quality_template_id:
            this.exporter_process_quality_template_id,
          registered_at: this.registered_at_formatted,
          exported_batch_id: this.exported_batch_id,
          samplings: this.samplings,
          quality_samplings: this.quality_samplings,
        });
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.gotToContainer();
      } catch (error) {
        console.log(error);
      }
    },
    submit_without_samplings: async function () {
      try {
        let response = await Requester.createWithoutSamplings({
          container_id: this.container_id,
          exporter_process_quality_template_id:
            this.exporter_process_quality_template_id,
          registered_at: this.registered_at_formatted,
          exported_batch_id: this.exported_batch_id,
          quality_generals: this.quality_generals,
        });
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.gotToContainer();
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>
